/**
 * Calendar math for the Dashboard's date filter, all of it UTC.
 *
 * The API buckets days in UTC, so the picker must reckon in UTC too — a
 * local-midnight "today" one hour ahead of UTC would ask for a day the server
 * has not started yet. Days travel as `YYYY-MM-DD` strings everywhere;
 * `Date` objects appear only inside the arithmetic.
 *
 * Weeks start on Monday, matching how the team reads a week; the vendor's
 * picker started them on Sunday and nobody will miss that.
 */

export interface DateRange {
  /** `YYYY-MM-DD`, inclusive. */
  from: string;
  /** `YYYY-MM-DD`, inclusive. */
  to: string;
}

export const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_2', label: 'Last 2 Days' },
  { key: 'last_7', label: 'Last 7 Days' },
  { key: 'last_14', label: 'Last 14 Days' },
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'last_90', label: 'Last 90 Days' },
] as const;

export type RangePresetKey = (typeof RANGE_PRESETS)[number]['key'];

const DAY_MS = 24 * 60 * 60 * 1000;

const dateOf = (day: string): Date => new Date(`${day}T00:00:00.000Z`);

export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function shiftDay(day: string, by: number): string {
  return new Date(dateOf(day).getTime() + by * DAY_MS).toISOString().slice(0, 10);
}

export function presetRange(key: RangePresetKey, now: Date = new Date()): DateRange {
  const to = todayUtc(now);
  switch (key) {
    case 'today':
      return { from: to, to };
    case 'this_week': {
      // Monday-first: Sunday (getUTCDay 0) is six days INTO the week.
      const back = (dateOf(to).getUTCDay() + 6) % 7;
      return { from: shiftDay(to, -back), to };
    }
    case 'this_month':
      return { from: `${to.slice(0, 8)}01`, to };
    case 'last_2':
      return { from: shiftDay(to, -1), to };
    case 'last_7':
      return { from: shiftDay(to, -6), to };
    case 'last_14':
      return { from: shiftDay(to, -13), to };
    case 'last_30':
      return { from: shiftDay(to, -29), to };
    case 'last_90':
      return { from: shiftDay(to, -89), to };
  }
}

/** The preset a range currently IS, for the checkmark. Null when custom. */
export function matchingPreset(range: DateRange, now: Date = new Date()): RangePresetKey | null {
  for (const preset of RANGE_PRESETS) {
    const candidate = presetRange(preset.key, now);
    if (candidate.from === range.from && candidate.to === range.to) return preset.key;
  }
  return null;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** `2026-08-20` → `20 Aug 2026`, the trigger and input display format. */
export function displayDay(day: string): string {
  const date = dateOf(day);
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]!.slice(0, 3)} ${date.getUTCFullYear()}`;
}

/** The trigger button's text: a preset's name when it is one, else the span. */
export function rangeLabel(range: DateRange, now: Date = new Date()): string {
  const preset = matchingPreset(range, now);
  if (preset) return RANGE_PRESETS.find((p) => p.key === preset)!.label;
  return `${displayDay(range.from)} → ${displayDay(range.to)}`;
}

export interface Month {
  year: number;
  /** 0-based, like Date. */
  month: number;
}

export function monthOf(day: string): Month {
  const date = dateOf(day);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
}

export function addMonths({ year, month }: Month, by: number): Month {
  const total = year * 12 + month + by;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

export function monthTitle({ year, month }: Month): string {
  return `${MONTHS[month]} ${year}`;
}

/**
 * The month as calendar weeks, Monday-first, each cell a `YYYY-MM-DD` or null
 * padding. Every row is exactly seven wide so the grid never staggers.
 */
export function monthGrid({ year, month }: Month): Array<Array<string | null>> {
  const first = new Date(Date.UTC(year, month, 1));
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7; // Monday-first offset

  const cells: Array<string | null> = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: Array<Array<string | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
